import { useEffect, useMemo, useRef, useState } from 'react'
import { SUPPORTED_LOCALES, type AppLocale, type AppSettings } from '../config/settings'
import { LOCALE_DISPLAY_NAMES } from '../i18n/locales'
import { t, tx } from '../i18n/translations'
import { loadSettings, updateSettings } from '../services/settings'
import { setBgmMuted, setSeMuted } from '../services/volume'
import { resumeAudioContext } from '../services/audioContext'
import {
  APP_COPYRIGHT,
  APP_DISPLAY_NAME,
  APP_SUPPORT_EMAIL,
  APP_VERSION,
} from '../config/app'
import {
  ACCOUNT_DELETE_ERRORS,
  ACCOUNT_DELETE_UI,
  ACCOUNT_RECOVER_UI,
  ACCOUNT_UNLINK_UI,
  AUTH_PREF_KEYS,
  AUTH_UI,
  type AuthProvider,
} from '../config/auth'
import { getPlatformId, isCapacitorNative } from '../utils/platform'
import {
  AuthCancelledError,
  commitAuthResult,
  getAuthState,
  getAvailableLoginProviders,
  signInWithApple,
  signInWithGoogle,
  signOut,
  subscribeAuthState,
  type AuthProfileHint,
  type AuthState,
} from '../services/auth'
import {
  getLinkedProviders,
  linkAccount,
  recoverAccount,
  unlinkAppleAccount,
  unlinkGoogleAccount,
  type LinkProvider,
} from '../services/accountLink'
import { deleteAccount, type DeleteResult } from '../services/accountDelete'
import { showAdPrivacyOptions } from '../services/ads'
import { LegalModal, type LegalType } from './LegalModal'

interface Props {
  open: boolean
  onClose: () => void
  /** 設定変更が保存された直後に呼ばれる（上位で反映したいとき用）。 */
  onSettingsChange?: (next: AppSettings) => void
  /**
   * BGM を OFF → ON にした直後に呼ばれる。autoplay ポリシーで paused のまま
   * 残っている BGM を、このユーザージェスチャ内で再生開始させるためのフック。
   */
  onSoundEnabled?: () => void
}

type SettingsTab = 'general' | 'account' | 'info'

/**
 * 設定モーダル。設定 / アカウント / 情報 の 3 タブ構成（Sweeper 準拠）。
 *
 * - プラットフォーム分岐:
 *    - LINE Mini App では「アカウント」タブを非表示にする（連携機能が未提供のため）
 *    - Capacitor Native 以外では「情報」タブに不適切な広告報告リンクを出さない
 * - 音量は ON/OFF のみ（Sweeper と同じトグル UI）。`bgmMuted` / `seMuted` を保存し、
 *   同時に `services/volume.ts` のストアにも反映して再生中の BGM / 次回以降の SE に
 *   即時伝播する。
 */
export function SettingsModal({
  open,
  onClose,
  onSettingsChange,
  onSoundEnabled,
}: Props) {
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings())
  // アカウントタブは Android / iOS（Capacitor ネイティブ）のみ表示。
  // LINE Mini App / Web ブラウザでは連携 UI を出さない。
  const showAccountTab = isCapacitorNative()
  // AccountTab の長時間処理（退会 / 連携解除 / アカウント復旧）が走っている間は
  // モーダル close を抑止し、エラー表示が消えてしまうのを防ぐ。
  const [accountBusy, setAccountBusy] = useState(false)
  const [activeTab, setActiveTab] = useState<SettingsTab>('general')
  const [legal, setLegal] = useState<LegalType | null>(null)
  const [copyNotice, setCopyNotice] = useState<string | null>(null)

  // open ごとに最新設定を読み直し（モーダルを開くたびに常に新鮮な値を表示）
  useEffect(() => {
    if (!open) return
    setSettings(loadSettings())
    // LINE Mini App でアカウントタブを選択状態のまま再オープンしたときの安全策
    if (!showAccountTab && activeTab === 'account') {
      setActiveTab('general')
    }
  }, [open, showAccountTab, activeTab])

  // ESC で閉じる。法律モーダル / アカウント処理中は閉じない。
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (legal) return
        if (accountBusy) return
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, legal, accountBusy, onClose])

  // close ボタン / オーバーレイクリックで使う共通ガード。
  // アカウント処理中はクリックを無視する（退会失敗時のエラーが消えるのを防ぐ）。
  const safeClose = () => {
    if (accountBusy) return
    onClose()
  }

  // 背景スクロール抑止
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) return null

  const applyPartial = (partial: Partial<AppSettings>) => {
    // BGM を OFF → ON にするときは、タイトルのトグルと同じ順序を踏む。
    // ユーザージェスチャ内で同期的に resume しないと、iOS / autoplay ポリシーで
    // paused のままの BGM が次のトラック切替まで鳴らない。
    const enablingBgm = settings.bgmMuted && partial.bgmMuted === false
    if (enablingBgm) resumeAudioContext()

    const next = updateSettings(partial)
    setSettings(next)
    // 永続化（updateSettings）と実行時ストア（volume.ts）の両方を更新する。
    // 永続化だけだと再生中の BGM / 次回の SE に伝わらない。
    if (partial.bgmMuted !== undefined) setBgmMuted(next.bgmMuted)
    if (partial.seMuted !== undefined) setSeMuted(next.seMuted)
    onSettingsChange?.(next)

    // 実効音量を反映した後に、paused のままの BGM を再生開始させる。
    if (enablingBgm) onSoundEnabled?.()
  }

  const handleToggleBgm = () => {
    applyPartial({ bgmMuted: !settings.bgmMuted })
  }
  const handleToggleSe = () => {
    applyPartial({ seMuted: !settings.seMuted })
  }
  const handleLocaleChange = (locale: AppLocale) => {
    applyPartial({ locale })
    document.documentElement.lang = locale
  }

  const tabs: SettingsTab[] = showAccountTab
    ? ['general', 'account', 'info']
    : ['general', 'info']

  return (
    <div
      className="settings-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-modal-title"
      onClick={safeClose}
    >
      <div
        className="settings-modal__dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="settings-modal__header">
          <h2 id="settings-modal-title" className="settings-modal__title">
            {t('settings')}
          </h2>
          <button
            type="button"
            className="settings-modal__close"
            aria-label={t('closeAria')}
            onClick={safeClose}
            disabled={accountBusy}
          >
            ×
          </button>
        </header>

        <nav className="settings-modal__tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={`settings-modal__tab${
                activeTab === tab ? ' settings-modal__tab--active' : ''
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'general'
                ? t('settingsTabGeneral')
                : tab === 'account'
                ? t('settingsTabAccount')
                : t('settingsTabInfo')}
            </button>
          ))}
        </nav>

        <div
          className="settings-modal__body"
          style={{ touchAction: 'pan-y' }}
        >
          {activeTab === 'general' && (
            <GeneralTab
              bgmOn={!settings.bgmMuted}
              seOn={!settings.seMuted}
              locale={settings.locale}
              onToggleBgm={handleToggleBgm}
              onToggleSe={handleToggleSe}
              onLocaleChange={handleLocaleChange}
            />
          )}

          {activeTab === 'account' && showAccountTab && (
            <AccountTab onBusyChange={setAccountBusy} />
          )}

          {activeTab === 'info' && (
            <InfoTab
              onShowLegal={(t) => setLegal(t)}
              copyNotice={copyNotice}
              setCopyNotice={setCopyNotice}
            />
          )}
        </div>

        <footer className="settings-modal__footer">
          <button
            type="button"
            className="btn btn--primary"
            onClick={onClose}
          >
            {t('close')}
          </button>
        </footer>
      </div>

      {legal && (
        <LegalModal
          open={legal !== null}
          type={legal}
          onClose={() => setLegal(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// 設定タブ（音量 ON/OFF）
// ---------------------------------------------------------------------------

interface GeneralTabProps {
  bgmOn: boolean
  seOn: boolean
  locale: AppLocale
  onToggleBgm: () => void
  onToggleSe: () => void
  onLocaleChange: (locale: AppLocale) => void
}

function GeneralTab({
  bgmOn,
  seOn,
  locale,
  onToggleBgm,
  onToggleSe,
  onLocaleChange,
}: GeneralTabProps) {
  return (
    <>
      <section className="settings-section">
        <label className="settings-section__label" htmlFor="settings-locale">
          {t('language')}
        </label>
        <select
          id="settings-locale"
          className="settings-select"
          value={locale}
          onChange={(e) => onLocaleChange(e.target.value as AppLocale)}
        >
          {SUPPORTED_LOCALES.map((item) => (
            <option key={item} value={item}>
              {LOCALE_DISPLAY_NAMES[item]}
            </option>
          ))}
        </select>
        <p className="settings-section__note">
          {t('languageNote')}
        </p>
      </section>

      <section className="settings-section">
        <div className="settings-toggle-row">
          <span className="settings-toggle-row__label">{t('bgm')}</span>
          <button
            type="button"
            role="switch"
            aria-checked={bgmOn}
            aria-label="BGM ON/OFF"
            className={`settings-toggle${bgmOn ? ' settings-toggle--on' : ''}`}
            onClick={onToggleBgm}
          >
            <span className="settings-toggle__knob" aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="settings-section">
        <div className="settings-toggle-row">
          <span className="settings-toggle-row__label">{t('se')}</span>
          <button
            type="button"
            role="switch"
            aria-checked={seOn}
            aria-label={`${t('se')} ON/OFF`}
            className={`settings-toggle${seOn ? ' settings-toggle--on' : ''}`}
            onClick={onToggleSe}
          >
            <span className="settings-toggle__knob" aria-hidden="true" />
          </button>
        </div>
      </section>
    </>
  )
}

// ---------------------------------------------------------------------------
// アカウントタブ（Android / iOS のみ表示）
//
// Sweeper / Balloon Float の `renderAccountTab()` レイアウトを Nawabari の BEM
// CSS 体系に移植したもの。挙動の差分:
//   - Sweeper は `accountLink.linkAppleAccount()` で「ゲスト→連携」する API を持つが、
//     Nawabari の `services/auth.ts` は `signInWithApple()` / `signInWithGoogle()` の
//     シンプルなログイン API のみ提供しているため、ここでは後者を呼ぶ。
//   - 状態の単一の真実は `getAuthState()` / `subscribeAuthState()` 経由で取得する。
//     `provider` 表示・ログアウトもこれに従って表示を切り替える。
//   - キャンセル (`AuthCancelledError`) はエラー扱いしない（ユーザー意図のため）。
// ---------------------------------------------------------------------------

const PROVIDER_LABEL: Record<string, string> = {
  google: 'Google',
  apple: 'Apple',
  line: 'LINE',
}

/**
 * 退会フローの失敗コードをユーザー向けメッセージに変換する。
 * `ACCOUNT_DELETE_ERRORS` の各コードに対応する文言は審査で必須となるため、
 * ハードコードせず `ACCOUNT_DELETE_UI` 系の定数で管理する余地を残しつつ、
 * 当面は現場で必要な最小限のマッピングだけここに置く。
 */
function describeDeleteFailure(result: Extract<DeleteResult, { ok: false }>): string {
  switch (result.code) {
    case ACCOUNT_DELETE_ERRORS.notAvailable:
      return 'この機能はアプリ版でのみ利用できます'
    case ACCOUNT_DELETE_ERRORS.notConfigured:
      return '退会機能を利用できる接続先が設定されていません'
    case ACCOUNT_DELETE_ERRORS.noSession:
      return 'セッションが切れています。再ログインしてからお試しください'
    case ACCOUNT_DELETE_ERRORS.storageIoError:
      return '端末ストレージにアクセスできませんでした。アプリを再起動してから再度お試しください'
    case ACCOUNT_DELETE_ERRORS.network:
      return 'ネットワークエラーが発生しました。電波状況を確認してください'
    case ACCOUNT_DELETE_ERRORS.noConfirmationToken:
    case ACCOUNT_DELETE_ERRORS.unknown:
    default:
      return ACCOUNT_DELETE_UI.failureFallback
  }
}

/**
 * 一括化した確認モーダルの種類。
 * 同じモーダル UI 上で「退会 / 既存アカウント復旧 / 連携解除」の 3 動作を扱う。
 * 復旧と連携解除は確定後に Hub 側へ通信を行うため、`profile` / `idToken` などの
 * コンテキストを Confirm 状態にぶら下げて、確定時に取り直し不要にしている。
 */
type ConfirmKind =
  | { kind: 'delete' }
  | { kind: 'recover'; provider: LinkProvider; idToken: string; profile?: AuthProfileHint }
  | { kind: 'unlink'; provider: LinkProvider }

function AccountTab({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }) {
  // 認証状態は SDK 側のシングルトンが真実なので、外部更新（ログイン直後の commit や
  // 401 → 再ログインフロー）にも追随できるよう subscribe しておく。
  const [authState, setAuthState] = useState<AuthState>(() => getAuthState())
  // どのプロバイダのフローが進行中か。`null` 以外の間は他プロバイダのボタンを disable。
  const [pendingProvider, setPendingProvider] = useState<AuthProvider | null>(null)
  const [signingOut, setSigningOut] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null)
  // 退会・復旧・連携解除を一括で扱う確認モーダル状態。
  const [confirm, setConfirm] = useState<ConfirmKind | null>(null)
  // 退会の進行中フラグ（確認モーダル close → /users/me/delete-confirm 完了までボタンを無効化）。
  const [deleting, setDeleting] = useState(false)
  // Hub に紐付いているプロバイダ一覧。連携解除ボタンの表示判定に使う。
  const [linkedProviders, setLinkedProviders] = useState<LinkProvider[]>([])
  const mountedRef = useRef(true)
  // 連携解除ボタン表示は Capacitor Native 限定。
  const platform = getPlatformId()
  const isNative = isCapacitorNative()
  // サインイン UI も AuthLoginModal と同じヘルパーで platform 別に出し分ける。
  // Apple Sign-In は iOS 専用（Android で押しても auth.ts で throw する）。
  const availableProviders = useMemo(() => getAvailableLoginProviders(), [])

  // 親（SettingsModal）に「処理中」を通知。閉じるボタン / オーバーレイクリックを抑止し、
  // 失敗時のエラーメッセージが消えないようにする。
  useEffect(() => {
    onBusyChange?.(deleting || signingOut || pendingProvider !== null)
  }, [deleting, signingOut, pendingProvider, onBusyChange])

  useEffect(() => {
    mountedRef.current = true
    const unsubscribe = subscribeAuthState((next) => {
      if (mountedRef.current) setAuthState(next)
    })
    return () => {
      mountedRef.current = false
      unsubscribe()
    }
  }, [])

  // 連携状態は Hub 側 (/auth/providers) が真の状態。マウント時 + サインイン状態が
  // 変わったタイミングで取り直し、UI（連携解除ボタンの表示・連携済バッジ）に反映する。
  // `getLinkedProviders` は内部でエラーハンドリングして空配列を返すため、ここで
  // try/catch する必要はない（fallback:ok）。
  useEffect(() => {
    if (!isNative) {
      // Capacitor Native 以外ではどのみち連携 UI を出さないので取得しない。
      setLinkedProviders([])
      return
    }
    let cancelled = false
    void (async () => {
      const providers = await getLinkedProviders()
      if (!cancelled && mountedRef.current) setLinkedProviders(providers)
    })()
    return () => {
      cancelled = true
    }
  }, [isNative, authState.status])

  // 一定時間で notice を自動消去（コピーした等の軽い通知用）。
  useEffect(() => {
    if (!noticeMessage) return
    const id = window.setTimeout(() => {
      if (mountedRef.current) setNoticeMessage(null)
    }, 2000)
    return () => window.clearTimeout(id)
  }, [noticeMessage])

  const isSignedIn = authState.status === 'signed_in'
  const currentProvider = isSignedIn ? authState.provider ?? null : null

  /**
   * Apple/Google でログイン → Hub の /auth/link でゲストアカウントに連携。
   *
   * 重要な分岐:
   *   - 連携成功 (`ok: true`) → `commitAuthResult` で UI 状態を反映。
   *   - `needsRecoveryConfirm` (Hub 側に既存アカウントあり) → 確認モーダルを出して
   *     ユーザー同意後に `recoverAccount` を呼ぶ（`runRecover`）。
   *     **注意**: ここで自動的に recoverAccount を呼ぶと、現在のゲストデータが上書き
   *     されて消えるリスクがある（SDK パリティルール sdk-parity.md / autoRecover=false）。
   *
   * これらの分岐は `services/accountLink.ts:linkAccount` 側で組み立て済みのため、
   * UI は `LinkResult` の判別ユニオンを見て分岐するだけ。
   */
  const runSignIn = async (provider: AuthProvider) => {
    setErrorMessage(null)
    setNoticeMessage(null)
    setPendingProvider(provider)
    try {
      if (provider !== 'google' && provider !== 'apple') {
        if (mountedRef.current) setErrorMessage(AUTH_UI.unsupportedMessage)
        return
      }
      const result =
        provider === 'google'
          ? await signInWithGoogle()
          : await signInWithApple()
      const link = await linkAccount(provider, result.idToken)

      if (link.ok) {
        await commitAuthResult(result)
        // 連携が成立したので /auth/providers 結果が変わる。再取得して UI に反映。
        const providers = await getLinkedProviders()
        if (mountedRef.current) {
          setLinkedProviders(providers)
          setNoticeMessage(tx('loggedInWith', { provider: PROVIDER_LABEL[provider] }))
        }
        return
      }

      if (link.needsRecoveryConfirm) {
        // 既存アカウントが連携先にある。確認モーダルを出してユーザーに「引き継ぐ」を選ばせる。
        if (mountedRef.current) {
          setConfirm({
            kind: 'recover',
            provider,
            idToken: result.idToken,
            profile: result.profile,
          })
        }
        return
      }

      // それ以外の失敗は明示通知（サーバ側のメッセージを優先）。
      const msg = link.message ?? AUTH_UI.errorMessage
      console.warn(`[AccountTab] linkAccount(${provider}) failed:`, link)
      if (mountedRef.current) setErrorMessage(msg)
    } catch (err) {
      if (err instanceof AuthCancelledError) {
        // ユーザー操作によるキャンセルはエラー表示しない。
        return
      }
      const msg = err instanceof Error ? err.message : AUTH_UI.errorMessage
      console.warn(`[AccountTab] signIn(${provider}) failed:`, err)
      if (mountedRef.current) setErrorMessage(msg)
    } finally {
      if (mountedRef.current) setPendingProvider(null)
    }
  }

  /**
   * ConfirmKind=recover の確定処理。
   * /auth/recover を呼んで session を復旧し、UI 状態を signed_in に切り替える。
   * 現在のゲスト/ローカル状態は Hub 側の sessionToken に切り替わる前提でリロードする
   * （ローカルキャッシュのプロフィール / スコアが古くなるため）。
   */
  const runRecover = async (action: Extract<ConfirmKind, { kind: 'recover' }>) => {
    setErrorMessage(null)
    setNoticeMessage(null)
    setPendingProvider(action.provider)
    try {
      const res = await recoverAccount(action.provider, action.idToken)
      if (res.ok) {
        // recoverAccount 内で session token を Preferences に保存済み。
        // commitAuthResult で UI と auth state を反映してからリロードする。
        await commitAuthResult({
          idToken: action.idToken,
          provider: action.provider,
          profile: action.profile,
        })
        if (mountedRef.current) {
          setNoticeMessage(ACCOUNT_RECOVER_UI.successMessage)
        }
        // ローカルストアやキャッシュは guest セッション基準で組み上がっているため、
        // 新セッションでクリーンに再構築するためにリロードする。
        window.setTimeout(() => {
          window.location.reload()
        }, ACCOUNT_RECOVER_UI.reloadDelayMs)
        return
      }
      console.warn(`[AccountTab] recover(${action.provider}) failed:`, res)
      if (mountedRef.current) {
        setErrorMessage(res.message ?? ACCOUNT_RECOVER_UI.failureFallback)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : ACCOUNT_RECOVER_UI.failureFallback
      console.warn(`[AccountTab] recover unexpected error:`, err)
      if (mountedRef.current) setErrorMessage(msg)
    } finally {
      if (mountedRef.current) setPendingProvider(null)
    }
  }

  /**
   * ConfirmKind=unlink の確定処理。
   * 内部で SocialLogin を再認証 → /auth/unlink → 成功時はローカル signOut。
   *
   * unlink の理由: Apple/Google ストア審査は退会導線を要求するが、
   * 「アカウントを保持したまま連携だけ切る」UX も提供しておくと、
   * 「別 Google アカウントに付け替えたい」「Apple→Google に移行したい」要望に応えられる。
   */
  const runUnlink = async (action: Extract<ConfirmKind, { kind: 'unlink' }>) => {
    setErrorMessage(null)
    setNoticeMessage(null)
    setPendingProvider(action.provider)
    try {
      const res =
        action.provider === 'apple'
          ? await unlinkAppleAccount()
          : action.provider === 'google'
          ? await unlinkGoogleAccount()
          : null
      if (!res) {
        if (mountedRef.current) setErrorMessage(AUTH_UI.unsupportedMessage)
        return
      }
      if (res.ok) {
        // Hub 側で連携が外れたので、ローカルもゲスト相当に戻すため signOut する。
        // signOut は @capgo/capacitor-social-login のセッションも明示的に終了させる。
        await signOut()
        const providers = await getLinkedProviders()
        if (mountedRef.current) {
          setLinkedProviders(providers)
          setNoticeMessage(ACCOUNT_UNLINK_UI.successMessage)
        }
        return
      }
      console.warn(`[AccountTab] unlink(${action.provider}) failed:`, res)
      if (mountedRef.current) {
        setErrorMessage(res.message ?? ACCOUNT_UNLINK_UI.failureFallback)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : ACCOUNT_UNLINK_UI.failureFallback
      console.warn(`[AccountTab] unlink unexpected error:`, err)
      if (mountedRef.current) setErrorMessage(msg)
    } finally {
      if (mountedRef.current) setPendingProvider(null)
    }
  }

  const runSignOut = async () => {
    setErrorMessage(null)
    setSigningOut(true)
    try {
      await signOut()
      if (mountedRef.current) {
        setNoticeMessage(t('logoutSuccess'))
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('logoutFailed')
      console.warn('[AccountTab] signOut failed:', err)
      if (mountedRef.current) setErrorMessage(msg)
    } finally {
      if (mountedRef.current) setSigningOut(false)
    }
  }

  // 退会ボタンは Capacitor ネイティブ AccountTab 内でのみ表示する設計。
  // 確認モーダルで明示同意 → 2-step 削除 → ローカル状態クリア → ページ再読み込み で
  // ゲストとして bootstrap し直す。失敗時はトースト相当のテキストで通知し、UI を残す。
  const runDeleteAccount = async () => {
    setErrorMessage(null)
    setNoticeMessage(null)
    setDeleting(true)
    try {
      const result = await deleteAccount()
      if (result.ok) {
        // サーバー削除は成功。ローカルクリア失敗時は警告メッセージに切り替えてからリロード。
        const localClearFailed = 'localClearFailed' in result && result.localClearFailed
        const message = localClearFailed
          ? '退会は完了しましたが、端末側のデータ削除に失敗しました。アプリを再起動してください。'
          : ACCOUNT_DELETE_UI.successMessage
        if (mountedRef.current) {
          setNoticeMessage(message)
        }
        // ローカル state（zustand 等）と SDK インスタンスは module レベルに残るため、
        // 完全クリーンな状態で再起動するためにページをリロードする。
        // 通知メッセージを一瞬見せてからリロードする（ユーザーフィードバック）。
        window.setTimeout(() => {
          window.location.reload()
        }, ACCOUNT_DELETE_UI.reloadDelayMs)
        return
      }
      console.warn(
        `[AccountTab] deleteAccount failed: code=${result.code}` +
          (result.httpStatus ? `, http=${result.httpStatus}` : '') +
          (result.serverCode ? `, server=${result.serverCode}` : ''),
      )
      if (mountedRef.current) setErrorMessage(describeDeleteFailure(result))
    } catch (err) {
      // deleteAccount は throw しない契約だが、未知の例外が発生しても fail-closed で UI に通知する。
      const msg = err instanceof Error ? err.message : ACCOUNT_DELETE_UI.failureFallback
      console.warn('[AccountTab] deleteAccount unexpected error:', err)
      if (mountedRef.current) setErrorMessage(msg)
    } finally {
      if (mountedRef.current) setDeleting(false)
    }
  }

  return (
    <section className="settings-section">
      <div className="settings-section__label">{t('account')}</div>

      <div className="settings-account__status">
        {isSignedIn ? (
          <>
            <span className="settings-account__badge settings-account__badge--linked">
              {t('linked')}
            </span>
            <span className="settings-account__text">
              {currentProvider
                ? tx('loggedInWith', { provider: PROVIDER_LABEL[currentProvider] ?? currentProvider })
                : t('loggedIn')}
            </span>
          </>
        ) : (
          <>
            <span className="settings-account__badge">{t('guest')}</span>
            <span className="settings-account__text">
              {t('playingAsGuest')}
            </span>
          </>
        )}
      </div>

      {!isSignedIn && (
        <div className="settings-account__buttons">
          {availableProviders.includes('apple') && (
            <button
              type="button"
              className="settings-account__provider-btn settings-account__provider-btn--apple"
              onClick={() => void runSignIn('apple')}
              disabled={pendingProvider !== null}
              aria-busy={pendingProvider === 'apple'}
            >
              <svg className="settings-account__provider-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="#000"
                  d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                />
              </svg>
              <span>
                {pendingProvider === 'apple' ? t('appleLoginBusy') : t('appleLogin')}
              </span>
            </button>
          )}

          {availableProviders.includes('google') && (
            <button
              type="button"
              className="settings-account__provider-btn settings-account__provider-btn--google"
              onClick={() => void runSignIn('google')}
              disabled={pendingProvider !== null}
              aria-busy={pendingProvider === 'google'}
            >
              <svg className="settings-account__provider-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span>
                {pendingProvider === 'google' ? t('googleLoginBusy') : t('googleLogin')}
              </span>
            </button>
          )}
        </div>
      )}

      {isSignedIn && (
        <div className="settings-account__buttons">
          <button
            type="button"
            className="settings-account__provider-btn settings-account__provider-btn--logout"
            onClick={() => void runSignOut()}
            disabled={signingOut || pendingProvider !== null}
            aria-busy={signingOut}
          >
            {signingOut ? t('logoutBusy') : t('logout')}
          </button>
        </div>
      )}

      {/*
        連携解除ボタン: Hub 側で連携済みのプロバイダ毎に出す。
        Apple は iOS 限定（Android で Apple Sign-In はそもそも使えないため）。
        Google は iOS / Android 両方で表示。connectedProviders は /auth/providers の結果。
      */}
      {isNative && linkedProviders.length > 0 && (
        <div className="settings-account__buttons">
          {linkedProviders.includes('apple') && platform === 'ios' && (
            <button
              type="button"
              className="settings-account__provider-btn settings-account__provider-btn--unlink"
              onClick={() => {
                setErrorMessage(null)
                setNoticeMessage(null)
                setConfirm({ kind: 'unlink', provider: 'apple' })
              }}
              disabled={pendingProvider !== null}
              aria-busy={pendingProvider === 'apple'}
            >
              {pendingProvider === 'apple'
                ? ACCOUNT_UNLINK_UI.pendingLabel
                : `${ACCOUNT_UNLINK_UI.buttonLabelPrefix}（Apple）`}
            </button>
          )}
          {linkedProviders.includes('google') && (
            <button
              type="button"
              className="settings-account__provider-btn settings-account__provider-btn--unlink"
              onClick={() => {
                setErrorMessage(null)
                setNoticeMessage(null)
                setConfirm({ kind: 'unlink', provider: 'google' })
              }}
              disabled={pendingProvider !== null}
              aria-busy={pendingProvider === 'google'}
            >
              {pendingProvider === 'google'
                ? ACCOUNT_UNLINK_UI.pendingLabel
                : `${ACCOUNT_UNLINK_UI.buttonLabelPrefix}（Google）`}
            </button>
          )}
        </div>
      )}

      {/*
        退会導線は Capacitor ネイティブの AccountTab に常時表示する（ゲスト状態でも
        サーバ側にアカウントレコードはあるため）。Apple/Google ストア審査ガイドラインで
        明確な退会導線が要求されるため、ボタンは目立つ位置・専用色で表示する。
      */}
      <div className="settings-account__buttons settings-account__buttons--danger">
        <button
          type="button"
          className="settings-account__provider-btn settings-account__provider-btn--delete"
          onClick={() => {
            setErrorMessage(null)
            setNoticeMessage(null)
            setConfirm({ kind: 'delete' })
          }}
          disabled={deleting || pendingProvider !== null}
          aria-busy={deleting}
        >
          {deleting ? ACCOUNT_DELETE_UI.pendingLabel : ACCOUNT_DELETE_UI.buttonLabel}
        </button>
      </div>

      {errorMessage && (
        <p
          className="settings-section__note settings-section__note--error"
          role="alert"
        >
          {errorMessage}
        </p>
      )}
      {noticeMessage && (
        <p className="settings-section__note" role="status">
          {noticeMessage}
        </p>
      )}

      {confirm && (
        <ConfirmDialog
          confirm={confirm}
          busy={deleting || pendingProvider !== null}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const action = confirm
            setConfirm(null)
            if (action.kind === 'delete') {
              void runDeleteAccount()
            } else if (action.kind === 'recover') {
              void runRecover(action)
            } else if (action.kind === 'unlink') {
              void runUnlink(action)
            }
          }}
        />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// 確認モーダル（退会 / 復旧 / 連携解除を一つのコンポーネントに集約）
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  confirm: ConfirmKind
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}

function ConfirmDialog({ confirm, busy, onCancel, onConfirm }: ConfirmDialogProps) {
  const text = confirmTextFor(confirm)
  // ESC で確認ダイアログだけを閉じる（SettingsModal 全体ではなく）。
  // capture フェーズで stopPropagation することで、外側 SettingsModal の ESC ハンドラが
  // モーダル本体まで閉じてしまうのを防ぐ。
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (busy) return
      e.stopPropagation()
      onCancel()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [busy, onCancel])

  return (
    <div className="settings-confirm__overlay" role="dialog" aria-modal="true">
      <div className="settings-confirm__dialog">
        <h3 className="settings-confirm__title">{text.title}</h3>
        <p className="settings-confirm__message">{text.message}</p>
        <div className="settings-confirm__actions">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onCancel}
            disabled={busy}
          >
            {text.cancel}
          </button>
          <button
            type="button"
            className={text.dangerous ? 'btn btn--danger' : 'btn btn--primary'}
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy}
          >
            {text.confirm}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ConfirmText {
  title: string
  message: string
  cancel: string
  confirm: string
  /** true なら確定ボタンを赤系（btn--danger）にする。 */
  dangerous: boolean
}

function confirmTextFor(action: ConfirmKind): ConfirmText {
  switch (action.kind) {
    case 'delete':
      return {
        title: t('deleteConfirmTitle'),
        message: t('deleteConfirmMessage'),
        cancel: t('deleteConfirmCancel'),
        confirm: t('deleteConfirmContinue'),
        dangerous: true,
      }
    case 'recover':
      return {
        title: t('recoverConfirmTitle'),
        message: t('recoverConfirmMessage'),
        cancel: t('recoverConfirmCancel'),
        confirm: t('recoverConfirmContinue'),
        dangerous: false,
      }
    case 'unlink':
      return {
        title: t('unlinkConfirmTitle'),
        message: t('unlinkConfirmMessage'),
        cancel: t('unlinkConfirmCancel'),
        confirm: t('unlinkConfirmContinue'),
        dangerous: true,
      }
  }
}

// ---------------------------------------------------------------------------
// 情報タブ（Sweeper 準拠）
// ---------------------------------------------------------------------------

interface InfoTabProps {
  onShowLegal: (type: LegalType) => void
  copyNotice: string | null
  setCopyNotice: (value: string | null) => void
}

/** Sweeper の Info タブに揃えた運営者 / 連絡先情報（アプリ名のみ差し替え）。 */
const OPERATOR_NAME = '株式会社ヤマネコワークス'
const OPERATOR_NAME_EN = 'Yamaneko Works, Inc.'
const CONTACT_EMAIL = APP_SUPPORT_EMAIL

function InfoTab({ onShowLegal, copyNotice, setCopyNotice }: InfoTabProps) {
  const isNative = isCapacitorNative()

  const handleCopyId = async (uid: string) => {
    try {
      await navigator.clipboard.writeText(uid)
      setCopyNotice(t('idCopied'))
      setTimeout(() => setCopyNotice(null), 1500)
    } catch {
      // fallback:ok — クリップボード API が使えない環境では通知のみで継続
      setCopyNotice(t('copyFailed'))
      setTimeout(() => setCopyNotice(null), 1500)
    }
  }

  // アプリ情報 + 運営者 + 連絡先 + ユーザーID を Sweeper と同じ粒度で並べる。
  // ユーザー ID は `ymg_session_token` から読める userId ではなく、profile の userId
  // を見るのが正確だが、SettingsModal まで props で引き回すのを避けるため
  // localStorage の値を参照する（Sweeper と同じ運用）。キー名は `AUTH_PREF_KEYS.uid` で集約。
  const uid =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(AUTH_PREF_KEYS.uid)
      : null

  return (
    <>
      <section className="settings-section">
        <div className="settings-section__label">{t('appInfo')}</div>
        <dl className="settings-info-list">
          <div className="settings-info-list__row">
            <dt>{t('appName')}</dt>
            <dd>{APP_DISPLAY_NAME}</dd>
          </div>
          <div className="settings-info-list__row">
            <dt>{t('version')}</dt>
            <dd>{APP_VERSION}</dd>
          </div>
        </dl>
      </section>

      <section className="settings-section">
        <div className="settings-section__label">{t('legalInfo')}</div>
        <div className="settings-info-buttons">
          <button
            type="button"
            className="btn btn--secondary settings-info-button"
            onClick={() => onShowLegal('terms')}
          >
            {t('terms')}
          </button>
          <button
            type="button"
            className="btn btn--secondary settings-info-button"
            onClick={() => onShowLegal('privacy')}
          >
            {t('privacy')}
          </button>
        </div>
      </section>

      <section className="settings-section">
        <div className="settings-section__label">{t('operator')}</div>
        <dl className="settings-info-list">
          <div className="settings-info-list__row">
            <dt>{t('operator')}</dt>
            <dd>{OPERATOR_NAME}</dd>
          </div>
          <div className="settings-info-list__row">
            <dt>{t('contact')}</dt>
            <dd>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="settings-info-link"
              >
                {CONTACT_EMAIL}
              </a>
            </dd>
          </div>
          {uid && (
            <div className="settings-info-list__row">
              <dt>ID</dt>
              <dd>
                <button
                  type="button"
                  className="settings-info-id-btn"
                  onClick={() => void handleCopyId(uid)}
                  title={t('copyTap')}
                >
                  {uid.length > 20
                    ? `${uid.slice(0, 10)}...${uid.slice(-6)}`
                    : uid}
                </button>
              </dd>
            </div>
          )}
        </dl>
        {copyNotice && (
          <p className="settings-section__note">{copyNotice}</p>
        )}
      </section>

      {isNative && (
        <section className="settings-section">
          <div className="settings-section__label">{t('other')}</div>
          <div className="settings-info-buttons">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                `Ad Report - ${t('appTitle')}`,
              )}`}
              className="btn btn--secondary settings-info-button"
            >
              {t('reportAd')}
            </a>
            <button
              type="button"
              className="btn btn--secondary settings-info-button"
              onClick={() => void showAdPrivacyOptions()}
            >
              {t('privacyChoices')}
            </button>
          </div>
        </section>
      )}

      <p className="settings-copyright">
        {APP_COPYRIGHT} — {OPERATOR_NAME_EN}
      </p>
    </>
  )
}
